<script>
import { debounce } from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";
import { BeneficiosEnum } from "@/core/enums/beneficios";
import { contarErrosProjeto } from "@/core/utils/validacoesJustificativas";
import { formatJustificativasResponse, prepareSaveData, prepareSaveDataMateriais } from "./justificativas/utils/formatters.js";

const tipoEnum = {
  COLABORADORES: "colaboradores",
  SERVICOS_TERCEIROS: "servicos-terceiros",
  MATERIAIS_LI: "materiais",
  MATERIAIS_LB: "materiais-lb",
};
const tipoRelatorioEnum = {
  COLABORADORES: "justificativa-colaboradores-por-projetos",
  SERVICOS_TERCEIROS: "justificativa-terceiros-por-projetos",
  MATERIAIS_LI: "justificativa-materiais-por-projetos",
  MATERIAIS_LB: "justificativa-materiais-lb-por-projetos",
};
const tipoRelatorioImportEnum = {
  COLABORADORES: "colaboradores",
  SERVICOS_TERCEIROS: "terceiros",
  MATERIAIS_LI: "materiais",
  MATERIAIS_LB: "materiais-lb",
};

const opcoesEscolaridadeLB = [
  "graduado",
  "técnico de nível médio",
  "pós-graduado",
  "doutor",
  "mestre",
  "apoio técnico",
  "tecnólogo",
];

const opcoesEnquadramentosEmpresas = [
  "Registro e Manutenção de Marcas, Patentes e Cultivares",
  "Fornecedor de Materiais",
];

export default {
  components: {
    FormModal: () => import("@/components/form-modal.vue"),
    MonthlyFilter: () => import("@/components/monthly-filter.vue"),
    QueueDialog: () => import("@/components/queue-dialog.vue"),
    ResumoPendenciasJustificativas: () => import("@/components/resumo-pendencias-justificativas.vue"),
    TableColaboradores: () => import("./justificativas/components/TableColaboradores.vue"),
    TableTerceiros: () => import("./justificativas/components/TableTerceiros.vue"),
    TableMateriais: () => import("./justificativas/components/TableMateriais.vue"),
    TableMateriaisLB: () => import("./justificativas/components/TableMateriaisLB.vue"),
    ModalNotas: () => import("./justificativas/components/ModalNotas.vue"),
    ModalAtividades: () => import("./justificativas/components/ModalAtividades.vue"),
  },
  data: function () {
    return {
      debouncedLoad: () => {},
      projetos: [],
      somenteErros: false,
      contextoModal: "",
      /** @type {('colaboradores' | 'servicos-terceiros')} */
      tipoSelecionado: "colaboradores",
      tipos: [
        { label: "Colaboradores", id: tipoEnum.COLABORADORES },
        { label: "Serviços terceiros", id: tipoEnum.SERVICOS_TERCEIROS },
      ],
      isVisibleUploadFile: false,
      uploadFileData: {},
      tipoEnum: tipoEnum,
      isVisibleQueueDialog: false,
      selectedNotas: [],
      modalNotas: false,
      modalAtividades: false,
      selectedAtividades: [],
      selectedRow: {},
      modalJustificativa: false,
      modalJustificativaAgrupamento: false,
      modalAnexoComprobatorio: false,
      dataAnexoComprobatorio: {},
      opts: {
        projetosAnexoComprobatorio: [],
        selecaoProjeto: [
          {
            id: 1,
            value: "Sim",
          },
          {
            id: 2,
            value: "Não",
          },
        ],
      },
      projetosSelecionadosComprovacao: [],
      isVisibleSelecaoProjetosAnexoComprobatorio: false,
      limiteJustificativaColaboradores: 3000,
      limiteJustificativaTerceiros: 1000,
      limiteFuncaoProjeto: 100,
      opcoesEscolaridadeLB,
      opcoesEnquadramentosEmpresas,
    };
  },
  created() {
    this.debouncedLoad = debounce(this.doLoad, 250);
  },
  watch: {
    clientId: function () {
      this.doLoad();
    },
  },
  computed: {
    ...mapGetters(["clientId", "intervaloCompetencia"]),
    // Benefício fixo em Lei de Informática (2) — o valor continua sendo
    // enviado como `trabalho`/`servico` nas chamadas que exigem o benefício
    servico() {
      return BeneficiosEnum.LEI_DE_INFORMATICA;
    },
    resumoPendencias() {
      return this.projetos.map((proj, idx) => ({
        key: this.projetoKey(proj) || idx,
        label: this.projetoLabel(proj),
        qtd: contarErrosProjeto(proj, this.servico),
      }));
    },
    projetosExibidos() {
      if (!this.somenteErros) return this.projetos;
      return this.projetos.filter(
        (proj) => contarErrosProjeto(proj, this.servico) > 0,
      );
    },
    tiposDisponiveis() {
      const basico = [...this.tipos];
      if (this.servico == 1 || this.servico == 3) {
        basico.push({ label: "Materiais", id: tipoEnum.MATERIAIS_LB });
      }
      if (this.servico == 2 || this.servico == 3) {
        basico.push({ label: "Materiais", id: tipoEnum.MATERIAIS_LI });
      }
      return basico;
    },
    pageTitle: function () {
      let titleSuffix = "Serviços Terceiros";
      if (this.tipoSelecionado === tipoEnum.COLABORADORES)
        titleSuffix = "Colaboradores";
      if (this.tipoSelecionado === tipoEnum.MATERIAIS_LI || this.tipoSelecionado === tipoEnum.MATERIAIS_LB)
        titleSuffix = "Materiais";
      return `Justificativas de ${titleSuffix} por projeto`;
    },
    resourceUrl() {
      return `/v1/projetos/${this.clientId}/justificativas/${this.tipoSelecionado}`;
    },
    resourceUrlLI() {
      return `/v1/projetos/${this.clientId}/justificativas/${this.tipoSelecionado}/li`;
    },
    relatorioXlsx: function () {
      let rows = this.projetos.reduce(
        (prev, { colaboradores, terceiros, materiais, total, projeto }) => {
          let listaExportacao = colaboradores || terceiros || materiais || [];
          let content = listaExportacao.map((item) => {
            const row = this.cols.map(({ value }) => item[value] || "");
            row.unshift(projeto.titulo || "");
            return row;
          });
          let totalRow = this.cols.map(({ value }) => total[value] || "");
          totalRow.splice(1, 0, "");
          return [...prev, ...content, totalRow];
        },
        [],
      );

      let styles = rows
        .reduce(
          (prev, row, rowIndex) => {
            const refs = [];

            if ([1, 2].includes(row.length)) {
              refs.push(rowIndex + 2);
            }

            row.forEach((cell, cellIndex) => {
              if (cell === "Total") {
                refs.push(rowIndex + 2);
              }

              if (typeof cell === "string" && cell.includes("R$ ")) {
                rows[rowIndex][cellIndex] = cell.replace("R$ ", "");
              }
            });

            return [...prev, ...refs];
          },
          [1],
        )
        .reduce(
          (acc, key) => ({
            ...acc,
            [key]: {
              font: {
                bold: true,
              },
            },
          }),
          {},
        );

      const columns = [
        {
          text: "Projeto",
          value: "projeto",
        },
        ...this.cols,
      ];

      return [
        rows,
        {
          columns,
          periodoCompetencia: this.competenciaReport,
          servico: this.servico,
          styles,
        },
      ];
    },
    competenciaReport: function () {
      let mIni = moment(this.intervaloCompetencia[0]);
      let mFim = moment(this.intervaloCompetencia[1]);
      return { ini: mIni.format("YYYY-MM"), fim: mFim.format("YYYY-MM") };
    },
    headerStyle: function () {
      return this.cols.map((h) => {
        if (h.value == "total") {
          return;
        }

        h.class = `vuetify-table-header${
          this.getClient().isClient ? "-cli" : ""
        }`;
        return h;
      });
    },
    uploadCols() {
      return [
        {
          key: "files",
          name: "Arquivo(s)",
          type: this.$fieldTypes.FILE,
          accept: ".xlsx",
          multiple: false,
        },
      ];
    },
    justificativaCols() {
      const isMateriaisLI = this.tipoSelecionado == tipoEnum.MATERIAIS_LI;
      const isMateriaisLB = this.tipoSelecionado == tipoEnum.MATERIAIS_LB;
      const isMateriais = isMateriaisLI || isMateriaisLB;
      const options = [
        {
          key: "tipo",
          name: "Tipo",
          type: this.$fieldTypes.SELECT,
          rows: {
            id: "id",
            nome: "value",
            data: [
              { id: "Material de Protótipo", value: "Material de Protótipo" },
              { id: "Material de Consumo", value: "Material de Consumo" },
            ]
          },
          show: isMateriaisLI,
        },
        {
          key: "funcao_projeto",
          name: isMateriais ? "Descrição do Agrupamento" : "Função no projeto",
          type: this.$fieldTypes.TEXT,
          rules: [
            { rule: "required" },
            { rule: "max", params: { size: this.limiteFuncaoProjeto } },
          ],
          counter: this.limiteFuncaoProjeto,
          maxLength: this.limiteFuncaoProjeto,
          show:
            (this.servico == 1 && this.tipoSelecionado == tipoEnum.COLABORADORES) || isMateriais,
        },
        {
          // Terceiros (LI): reutiliza a coluna funcao_projeto como "Descrição",
          // preenchida em conjunto com a justificativa (sem alterar a estrutura da tabela).
          key: "funcao_projeto",
          name: "Descrição",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "max", params: { size: this.limiteFuncaoProjeto } }],
          counter: this.limiteFuncaoProjeto,
          maxLength: this.limiteFuncaoProjeto,
          show:
            this.servico == 2 && this.tipoSelecionado == tipoEnum.SERVICOS_TERCEIROS,
        },
        {
          key: "justificativa",
          name: "Justificativa",
          type: this.$fieldTypes.TEXTAREA,
          rules: [
            { rule: "required" },
            {
              rule: "max",
              params: {
                size:
                  this.tipoSelecionado == tipoEnum.COLABORADORES
                    ? this.limiteJustificativaColaboradores
                    : this.limiteJustificativaTerceiros,
              },
            },
          ],
          counter:
            this.tipoSelecionado == tipoEnum.COLABORADORES
              ? this.limiteJustificativaColaboradores
              : this.limiteJustificativaTerceiros,
          maxLength:
            this.tipoSelecionado == tipoEnum.COLABORADORES
              ? this.limiteJustificativaColaboradores
              : this.limiteJustificativaTerceiros,
          show: true,
        },
        {
          key: "formacao_li_descricao",
          name: "Descrição da formação (Outra)",
          type: this.$fieldTypes.TEXT,
          rules: [{ rule: "max", params: { size: 255 } }],
          counter: 255,
          maxLength: 255,
          show: this.servico == 2 && this.tipoSelecionado == tipoEnum.COLABORADORES && this.selectedRow.formacao === 'Outra',
        },
      ];

      return options.filter(({ show }) => show);
    },
    justificativaAgrupamentoCols() {
      return [
        {
          key: "agrupamento_justificativa",
          name: "Justificativa",
          type: this.$fieldTypes.TEXTAREA,
          rules: [
            { rule: "required" },
            { rule: "max", params: { size: 4000 } },
          ],
        },
      ];
    },

    actionBarButtons() {
      const buttons = [];
      buttons.push(
        {
          text: "Atualizar",
          icon: "mdi-refresh",
          show: true,
          action: () => {
            this.doLoad();
          },
        },
        {
          text: "Gerar anexo comprobatório",
          icon: "mdi-paperclip",
          show: this.servico == 1,
          action: () => {
            this.projetosSelecionadosComprovacao = [];
            this.dataAnexoComprobatorio = [];
            this.modalAnexoComprobatorio = true;
          },
        },
      );
      return buttons.filter(({ show }) => show);
    },
    anexoComprobatorioCols() {
      return [
        {
          key: "proj_selecao",
          name: "Gerar para todos os projetos?",
          type: this.$fieldTypes.RADIO,
          outline: true,
          labelBold: true,
          defaultValue: 1,
          rules: [{ rule: "required" }],
          rel: { to: "selecaoProjeto", key: "id", name: "value" },
        },
        {
          key: "projeto_id",
          name: "Projetos",
          type: this.$fieldTypes.AUTOCOMPLETE_MULTIPLE,
          rel: { to: "projetosAnexoComprobatorio", key: "id", name: "titulo" },
          hideInform: !this.isVisibleSelecaoProjetosAnexoComprobatorio,
        },
      ];
    },
  },
  methods: {
    projetoKey(proj) {
      return proj.projeto.agrupamento_id || proj.projeto.id;
    },
    projetoLabel(proj) {
      if (proj.projeto.agrupamento_id) {
        return proj.projeto.agrupamento_titulo || proj.projeto.agrupamento_codigo;
      }
      return proj.projeto.titulo || proj.projeto.codigo;
    },
    qtdPendencias(proj) {
      return contarErrosProjeto(proj, this.servico);
    },
    scrollToProjeto(key) {
      const ref = this.$refs["projeto-" + key];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    doLoad() {
      this.projetos = [];
      const useLIEndpoint = this.servico !== 1 && this.tipoSelecionado !== tipoEnum.MATERIAIS_LB;
      
      let workTrabalho = this.servico;
      if (this.tipoSelecionado === tipoEnum.MATERIAIS_LB) workTrabalho = 1;
      if (this.tipoSelecionado === tipoEnum.MATERIAIS_LI) workTrabalho = 2;

      this.apiResource(
        useLIEndpoint ? this.resourceUrlLI : this.resourceUrl,
      )
        .get({
          query: `competenciaIni=${this.intervaloCompetencia[0]}&competenciaFim=${this.intervaloCompetencia[1]}&trabalho=${workTrabalho}`,
        })
        .then((response) => {
          const formatted = formatJustificativasResponse(response);
          this.opts.projetosAnexoComprobatorio = formatted.projetosAnexoComprobatorio;
          this.projetos = formatted.projetos;

          if (this.tipoSelecionado == tipoEnum.SERVICOS_TERCEIROS) {
            this.validaCNPJDuplicadoMesmoProjeto();
          }
        });
    },
    async doSave(formData = {}, close) {
      try {
        let saveData = null;
        let url = `/v1/projetos/${this.clientId}/justificativas`;

        if (this.tipoSelecionado === tipoEnum.MATERIAIS_LI || this.tipoSelecionado === tipoEnum.MATERIAIS_LB) {
          const forceTrabalho = this.tipoSelecionado === tipoEnum.MATERIAIS_LI ? 2 : 1;
          saveData = prepareSaveDataMateriais(formData, forceTrabalho, this.intervaloCompetencia);
          url = `/v1/projetos/${this.clientId}/justificativas/materiais/batch`;
        } else {
          saveData = prepareSaveData(formData, this.tipoSelecionado, tipoEnum, this.servico);
        }

        await this.apiResource(url).save(
          { ...saveData },
          formData.justificativa_id,
        );

        if (close) {
          close();
        }

        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    saveDataAgrupamento: function (formData = {}) {
      const {
        agrupamento_justificativa_id,
        agrupamento_justificativa,
        id,
        projeto_id,
        funcao_projeto,
      } = formData;
      const isColaboradores =
        this.tipoSelecionado === tipoEnum.COLABORADORES ? true : false;
      const projeto = this.projetos.filter(
        (p) => p.projeto.id == projeto_id,
      )[0];

      return {
        model_id: id,
        model_type: isColaboradores ? "funcionarios" : "terceiros",
        projeto_id: projeto.projeto.agrupamento_id,
        justificativa: agrupamento_justificativa,
        agrupamento_justificativa: agrupamento_justificativa,
        agrupamento_justificativa_id: agrupamento_justificativa_id,
        funcao_projeto: funcao_projeto,
        trabalho: this.servico,
      };
    },
    async doSaveJustificativaAgrupamento(formData = {}, close) {
      try {
        const saveData = this.saveDataAgrupamento(formData);
        const url = `/v1/projetos/${this.clientId}/justificativas`;
        await this.apiResource(url).save(
          { ...saveData },
          saveData.agrupamento_justificativa_id,
        );
        if (close) {
          close();
        }

        this.doLoad();
      } catch (error) {
        this.notify(error);
      }
    },
    importXlsx() {
      this.uploadFileData = {};
      this.isVisibleUploadFile = true;
    },
    relatorioJustificativaImportRequest() {
      const uploadedFile = this.$store.getters.uploadedFile;
      let tipoUpload;
      
      if (this.tipoSelecionado == tipoEnum.COLABORADORES) {
        tipoUpload = tipoRelatorioImportEnum.COLABORADORES;
      } else if (this.tipoSelecionado == tipoEnum.MATERIAIS_LI) {
        tipoUpload = tipoRelatorioImportEnum.MATERIAIS_LI;
      } else if (this.tipoSelecionado == tipoEnum.MATERIAIS_LB) {
        tipoUpload = tipoRelatorioImportEnum.MATERIAIS_LB;
      } else {
        tipoUpload = tipoRelatorioImportEnum.SERVICOS_TERCEIROS;
      }
      const trabalhoForce = this.tipoSelecionado == tipoEnum.MATERIAIS_LI ? 2 : (this.tipoSelecionado == tipoEnum.MATERIAIS_LB ? 1 : this.servico);
      let formData = new FormData();
      formData.append("arquivo", uploadedFile);
      formData.append("tipo", tipoUpload);
      formData.append("trabalho", trabalhoForce);
      return formData;
    },
    doImportXls() {
      const uploadRequest = this.relatorioJustificativaImportRequest();
      this.apiResource(`v1/projetos/${this.clientId}/justificativas/importar`)
        .save(uploadRequest)
        .then((result) => {
          this.showNotification(result);
          this.isVisibleUploadFile = false;
        });
    },
    doEdit(row = {}) {
      this.selectedRow = row;
      this.modalJustificativa = true;
    },
    doEditAgrupamento(row = {}) {
      this.selectedRow = row;
      this.modalJustificativaAgrupamento = true;
    },
    showNotification(result) {
      const message = !result.error
        ? "Relatório enviado para a fila de importação com sucesso, aguarde."
        : "Erro ao importar o relatório";
      this.$notify({
        group: "geral",
        type: !result.error ? "success" : "error",
        text: message,
        title: message,
        duration: 5000,
      });
    },
    relatorioJustificativaRequest() {
      const { ini, fim } = this.competenciaReport;
      
      let tipoRelatorio;
      if (this.tipoSelecionado == tipoEnum.COLABORADORES) {
        tipoRelatorio = tipoRelatorioEnum.COLABORADORES;
      } else if (this.tipoSelecionado == tipoEnum.MATERIAIS_LI) {
         tipoRelatorio = tipoRelatorioEnum.MATERIAIS_LI;
      } else if (this.tipoSelecionado == tipoEnum.MATERIAIS_LB) {
         tipoRelatorio = tipoRelatorioEnum.MATERIAIS_LB;
      } else {
        tipoRelatorio = tipoRelatorioEnum.SERVICOS_TERCEIROS;
      }
      
      const trabalhoForce = this.tipoSelecionado == tipoEnum.MATERIAIS_LI ? 2 : (this.tipoSelecionado == tipoEnum.MATERIAIS_LB ? 1 : this.servico);
      return {
        tipoRelatorio,
        competenciaIni: ini,
        competenciaFim: fim,
        trabalho: trabalhoForce,
      };
    },
    exportacaoRelatorio() {
      const relatorioRequest = this.relatorioJustificativaRequest();
      this.apiResource(`v1/clientes/${this.clientId}/exportacao`)
        .save(relatorioRequest)
        .then((result) => {
          if (!result.error) this.isVisibleQueueDialog = true;
        });
    },
    openNotasModal(notas = [], item = {}) {
      this.selectedNotas = notas;
      this.contextoModal = item.razao_social || item.nome || item.descricao || "";
      this.modalNotas = true;
    },
    openAtividadesModal(atividades = [], item = {}) {
      this.selectedAtividades = atividades;
      this.contextoModal = item.nome || "";
      this.modalAtividades = true;
    },
    doSaveAnexoComprobatorio() {
      const resource = this.apiResource(
        `v1/projetos/${this.clientId}/justificativas/anexoComprobatorio`,
      );
      const bodySave = this.anexoComprobatorioModel();

      this.showConfirm(
        "Gerar anexo comprobatório",
        "Deseja realmente gerar o anexo comprobatório para o(s) projeto(s) selecionado(s)?",
        () => {
          resource.save(bodySave).then((result) => {
            this.$notify({
              type: "success",
              group: "geral",
              title: "Sucesso",
              text: result.message,
            });

            this.modalAnexoComprobatorio = false;
            return true;
          });
        },
      );
    },
    doChangeAnexoComprobatorio(formData = {}) {
      this.isVisibleSelecaoProjetosAnexoComprobatorio = false;

      if (formData.proj_selecao == 2) {
        const idsSelecionados = formData.projeto_id;
        this.isVisibleSelecaoProjetosAnexoComprobatorio = true;

        if (idsSelecionados) {
          this.projetosSelecionadosComprovacao = idsSelecionados;
        }
      }
    },
    anexoComprobatorioModel() {
      let projetosSelecionados = [];
      this.projetosSelecionadosComprovacao.map((id) => {
        projetosSelecionados.push(
          this.opts.projetosAnexoComprobatorio.filter(
            (proj) => proj.id == id,
          )[0],
        );
      });
      return {
        trabalho: this.tipoSelecionado == tipoEnum.MATERIAIS_LI ? 2 : (this.tipoSelecionado == tipoEnum.MATERIAIS_LB ? 1 : this.servico),
        tipo: this.tipoSelecionado,
        competenciaIni: this.intervaloCompetencia[0],
        competenciaFim: this.intervaloCompetencia[1],
        projetos: projetosSelecionados,
      };
    },
    validaCNPJDuplicadoMesmoProjeto() {
      const projetos = this.projetos.map((items) => {
        return {
          projeto_id: items.projeto.id ?? items.projeto.agrupamento_id,
          terceiros: items.terceiros.map((terceiro) => {
            return {
              terceiro_id: terceiro.id,
              terceiro_cnpj: terceiro.cnpj ?? terceiro.cpf,
              terceiro_enquadramento: terceiro.enquadramento,
            };
          }),
        };
      });

      for (const projeto of projetos) {
        const cnpjMap = new Map();

        for (const t of projeto.terceiros) {
          const chave = `${t.terceiro_cnpj}_${t.terceiro_enquadramento ?? "null"}`;

          if (cnpjMap.has(chave)) {
            this.projetos.forEach((p) => {
              const projetoId = p.projeto.id ?? p.projeto.agrupamento_id;
              if (projetoId === projeto.projeto_id) {
                p.terceiros.forEach((ter) => {
                  const cnpj = ter.cnpj ?? ter.cpf;
                  const enquadramento = ter.enquadramento ?? "null";

                  const chaveAtual = `${cnpj}_${enquadramento}`;
                  if (chaveAtual === chave) {
                    ter.hasDuplicidades = true;
                  }
                });
              }
            });
          } else {
            cnpjMap.set(chave, 1);
          }
        }
      }
    },
  },
};
</script>

<template>
  <div class="content">
    <div class="header d-flex justify-content-between align-items-center">
      <div class="title ml-3">
        <h3 class="h3 mb-n2 bold">{{ pageTitle }}</h3>
        <span class="h6 mt-n2 text-gray-400">
          Revise e justifique os dispêndios alocados em cada projeto
        </span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <v-btn
          v-for="(button, idx) in actionBarButtons"
          :key="'action-bar-' + idx"
          class="elevation-4 mr-2"
          :aria-label="'actionBarButtons-' + idx"
          @click="button.action"
        >
          <v-icon left>{{ button.icon }}</v-icon>
          <span style="font-size: 12px">{{ button.text }}</span>
        </v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="elevation-4 mr-2" v-on="on" v-bind="attrs">
              <v-icon left>mdi-file-chart-outline</v-icon>
              <span style="font-size: 12px">Relatório</span>
            </v-btn>
          </template>
          <v-list style="padding: 1rem">
            <v-list-item-content>
              <v-list-item-title
                class="table-v-action-button mr-3"
                @click="exportacaoRelatorio()"
                style="font-size: 0.81rem; font-weight: 400; cursor: pointer"
              >
                <v-icon>mdi-file-export-outline</v-icon>Exportação
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-content>
              <v-list-item-title
                class="table-v-action-button mr-3"
                @click="importXlsx()"
                style="font-size: 0.81rem; font-weight: 400; cursor: pointer"
              >
                <v-icon>mdi-file-import-outline</v-icon>Importação
              </v-list-item-title>
            </v-list-item-content>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div class="d-flex align-center mt-3 mb-2 ml-3">
      <v-select
        v-model="tipoSelecionado"
        prependIcon="mdi-format-list-bulleted-type"
        class="mr-3 pa-0 mb-0 mt-0"
        hide-details
        :items="tiposDisponiveis"
        itemText="label"
        itemValue="id"
        prefix="Tipo:"
        style="max-width: 270px"
        @change="debouncedLoad()"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title style="font-size: 0.81rem; font-weight: 500">
              {{ item.label }}
            </v-list-item-title>
          </v-list-item-content>
        </template>
      </v-select>
      <MonthlyFilter
        style="min-width: 270px"
        @change="debouncedLoad()"
      ></MonthlyFilter>
      <v-switch
        v-model="somenteErros"
        label="Somente pendências"
        hide-details
        class="ml-3 mt-0 pt-0"
        data-testid="switch-somente-pendencias"
      ></v-switch>
    </div>

    <ResumoPendenciasJustificativas
      v-if="projetos.length"
      class="mb-3"
      :itens="resumoPendencias"
      @navegar="scrollToProjeto"
    />

    <div class="body">
      <div
        v-if="!projetos.length"
        class="teros-elevation rounded white font-weight-bold d-block pa-3 text-center"
      >
        <span> Nenhum projeto retornado para os filtros selecionados. </span>
      </div>
      <div
        v-for="(proj, idx) in projetosExibidos"
        :key="idx + 'projeto'"
        :ref="'projeto-' + (projetoKey(proj) || idx)"
        class="projeto-card teros-elevation rounded white"
        :class="{ 'mt-6': idx != 0 }"
        data-testid="card-projeto"
      >
      <div
        class="projeto-header d-flex align-center justify-space-between px-4 py-2"
        data-testid="cabecalho-projeto"
      >
        <div class="d-flex align-center">
          <v-icon class="mr-3" color="#1E88E5">
            {{ proj.projeto.agrupamento_id ? "mdi-folder-multiple-outline" : "mdi-folder-outline" }}
          </v-icon>
          <div>
            <div class="projeto-header-titulo">
              {{ (proj.projeto.agrupamento_id ? proj.projeto.agrupamento_titulo : proj.projeto.titulo) || "Sem título" }}
            </div>
            <div class="projeto-header-meta">
              {{ proj.projeto.agrupamento_id ? "Projeto" : "Iniciativa" }}
              · Código: {{ (proj.projeto.agrupamento_id ? proj.projeto.agrupamento_codigo : proj.projeto.codigo) || "Sem código" }}
              · Ano-base: {{ (proj.projeto.agrupamento_id ? proj.projeto.agrupamento_anobase : proj.projeto.anoBase) || "Sem Ano-base" }}
            </div>
          </div>
        </div>
        <v-chip
          v-if="qtdPendencias(proj)"
          small
          outlined
          color="error"
          class="ml-2 font-weight-bold flex-shrink-0"
          data-testid="badge-pendencias-projeto"
        >
          <v-icon left small>mdi-alert-circle-outline</v-icon>
          {{ qtdPendencias(proj) }} {{ qtdPendencias(proj) === 1 ? "pendência" : "pendências" }}
        </v-chip>
        <v-chip
          v-else
          small
          outlined
          color="success"
          class="ml-2 flex-shrink-0"
          data-testid="badge-sem-pendencias-projeto"
        >
          <v-icon left small>mdi-check</v-icon>
          Sem pendências
        </v-chip>
      </div>
      <template v-if="tipoSelecionado === tipoEnum.COLABORADORES">
        <TableColaboradores
          :key="'colaboradores-' + (proj.projeto.agrupamento_id || proj.projeto.id || idx)"
          :projetoWrapper="proj"
          :trabalho="servico"
          :somenteErros="somenteErros"
          @edit="doEdit"
          @open-atividades="openAtividadesModal"
        />
      </template>
      <template v-else-if="tipoSelecionado === tipoEnum.SERVICOS_TERCEIROS">
        <TableTerceiros
          :key="'terceiros-' + (proj.projeto.agrupamento_id || proj.projeto.id || idx)"
          :projetoWrapper="proj"
          :trabalho="servico"
          :somenteErros="somenteErros"
          @edit="doEdit"
          @open-notas="openNotasModal"
        />
      </template>
      <template v-else-if="tipoSelecionado === tipoEnum.MATERIAIS_LI">
        <TableMateriais
          :key="'materiais-' + (proj.projeto.agrupamento_id || proj.projeto.id || idx)"
          :projetoWrapper="proj"
          :trabalho="servico"
          :somenteErros="somenteErros"
          @edit="doEdit"
          @open-notas="openNotasModal"
        />
      </template>
      <template v-else-if="tipoSelecionado === tipoEnum.MATERIAIS_LB">
        <TableMateriaisLB
          :key="'materiais-lb-' + (proj.projeto.agrupamento_id || proj.projeto.id || idx)"
          :projetoWrapper="proj"
          :trabalho="servico"
          :somenteErros="somenteErros"
          @edit="doEdit"
          @open-notas="openNotasModal"
        />
      </template>
      </div>
    </div>

    <FormModal
      title="Editar justificativa"
      :cols="justificativaCols"
      :opened.sync="modalJustificativa"
      :value.sync="selectedRow"
      @save="doSave"
    ></FormModal>

    <FormModal
      title="Editar justificativa agrupamento"
      :cols="justificativaAgrupamentoCols"
      :opened.sync="modalJustificativaAgrupamento"
      :value.sync="selectedRow"
      @save="doSaveJustificativaAgrupamento"
    ></FormModal>

    <FormModal
      title="Importação justificativa"
      :cols="uploadCols"
      :opened.sync="isVisibleUploadFile"
      :value.sync="uploadFileData"
      @save="doImportXls()"
    ></FormModal>

    <ModalNotas
      :opened.sync="modalNotas"
      :notas="selectedNotas"
      :contexto="contextoModal"
    />

    <ModalAtividades
      :opened.sync="modalAtividades"
      :atividades="selectedAtividades"
      :contexto="contextoModal"
    />

    <QueueDialog
      :opened.sync="isVisibleQueueDialog"
      title="Exportação justificativas"
      appendTitleIcon="mdi-file-chart-outline"
    ></QueueDialog>

    <FormModal
      title="Gerar anexo comprobatório"
      :cols="anexoComprobatorioCols"
      :opened.sync="modalAnexoComprobatorio"
      :value.sync="dataAnexoComprobatorio"
      :opts="opts"
      @save="doSaveAnexoComprobatorio"
      @update:value="doChangeAnexoComprobatorio"
    ></FormModal>
  </div>
</template>

<style lang="scss" scoped>
.notas-table-v {
  min-width: 500px;

  .v-data-table__wrapper {
    height: unset !important;
  }
}

.projeto-card {
  overflow: hidden;
}

.projeto-header {
  background-color: #f5f7fa;
  border-left: 4px solid #1e88e5;
}

.projeto-header-titulo {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1c1c44;
}

.projeto-header-meta {
  font-size: 0.75rem;
  color: #757575;
}
</style>
